import { useEffect, useState } from 'react';
import { AuthSession } from '@Shared/auth/auth-session';
import { type AuthContextValue } from '../contexts/auth/auth.context';
import { type IdTokenClaims } from '@Shared/auth/auth-profile';
import { useAuthUserFindMeQuery } from '../../data/user/hooks/use-auth-user-find-me.query';
import { useImpersonatedUserQuery } from '../../data/user/hooks/use-impersonated-user.query';
import { isValidPayroll } from '../../shared/utils/payroll.utils';

const TOKEN_RETRY_INTERVAL_MS = 300;
const TOKEN_MAX_RETRIES = 10;

export const useAuth = (): AuthContextValue | null | undefined => {
  const [token, setToken] = useState<string>('');
  const [authReady, setAuthReady] = useState(false);
  const [initialClaims, setInitialClaims] = useState<IdTokenClaims | null>(null);
  const [impersonatedEmployeeId, setImpersonatedEmployeeId] = useState<string | null>(null);
  const [isExtendedUserInfoError, setIsExtendedUserInfoError] = useState(false);

  const {
    data: authUser,
    isLoading: isLoadingAuthUser,
    refetch: authRefetch,
  } = useAuthUserFindMeQuery(token, { enabled: false });

  const {
    data: impersonatedUserData,
    refetch: userRefetch,
    isError: isUserInfoError,
  } = useImpersonatedUserQuery(impersonatedEmployeeId ?? '', token, { enabled: false });

  // Reintenta leer el token del sessionStorage hasta que el DPX lo escriba
  useEffect(() => {
    let attempt = 0;
    let timeoutId: ReturnType<typeof setTimeout>;

    const tryReadToken = () => {
      const idToken = AuthSession.getInstance().getIdToken();
      const idTokenClaims = AuthSession.getInstance().getIdTokenClaims();

      if (idToken && idTokenClaims) {
        setToken(idToken);
        setInitialClaims(idTokenClaims);
        setAuthReady(true);
        return;
      }

      attempt++;
      if (attempt >= TOKEN_MAX_RETRIES) {
        setAuthReady(true);
      } else {
        timeoutId = setTimeout(tryReadToken, TOKEN_RETRY_INTERVAL_MS);
      }
    };

    tryReadToken();
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (token) {
      authRefetch().catch(() => {});
    }
  }, [token, authRefetch]);

  useEffect(() => {
    if (
      isValidPayroll(impersonatedEmployeeId) &&
      impersonatedEmployeeId !== initialClaims?.employeeid
    ) {
      userRefetch().catch(() => {});
    }
  }, [impersonatedEmployeeId]);

  useEffect(() => {
    setIsExtendedUserInfoError(isUserInfoError);
  }, [isUserInfoError]);

  const isAuthError = authReady && !token;

  if (!authReady) return undefined;
  if (isAuthError || !initialClaims) return null;
  if (isLoadingAuthUser || (token && !authUser)) return undefined;
  if (!authUser) return null;

  const { isCrewAdmin } = authUser;
  const isImpersonating = impersonatedEmployeeId !== null;
  const claims = isImpersonating
    ? { ...initialClaims, employeeid: impersonatedEmployeeId }
    : initialClaims;

  const impersonate = (employeeId: string) => {
    if (!isValidPayroll(employeeId)) {
      setIsExtendedUserInfoError(true);
      return;
    }
    setIsExtendedUserInfoError(false);
    setImpersonatedEmployeeId(employeeId);
  };

  const stopImpersonation = () => {
    setImpersonatedEmployeeId(null);
    setIsExtendedUserInfoError(false);
    location.reload();
  };

  return {
    token,
    claims,
    initialClaims,
    isCrewAdmin,
    impersonate,
    stopImpersonation,
    isImpersonating,
    isError: isExtendedUserInfoError,
    impersonatedUser: impersonatedUserData ?? null,
  };
};
